import React, { useState } from 'react';
import FinanceService from '../services/FinanceService';

const errorMessage = (error, fallback) =>
  error?.response?.data?.error || fallback;

export default function MercadoPagoConnectButton({ account, canManage, showAlert, onConnected }) {
  const [open, setOpen] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [oauth, setOauth] = useState({ clientId: '', clientSecret: '', redirectUri: '', code: '', state: '' });
  const [authorizationUrl, setAuthorizationUrl] = useState('');
  const eligible = canManage && account?.integrationType === 'API' && Number(account?.id) > 0;
  if (!eligible) return null;

  const connected = async (data) => {
    setAccessToken('');
    setOauth((current) => ({ ...current, clientSecret: '', code: '' }));
    setOpen(false);
    showAlert?.('success', 'Mercado Pago conectado. A sincronização inicial já foi solicitada.');
    await onConnected?.(data);
  };

  const connectDirect = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { data } = await FinanceService.connectMercadoPago(account.id, accessToken);
      await connected(data);
    } catch (error) {
      showAlert?.('error', errorMessage(error, 'Não foi possível validar o Access Token.'));
    } finally { setBusy(false); }
  };

  const startOAuth = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { data } = await FinanceService.startMercadoPagoOAuth(account.id, oauth);
      setAuthorizationUrl(data.authorizationUrl || '');
      setOauth((current) => ({ ...current, clientSecret: '', state: data.state || '' }));
      if (data.authorizationUrl) window.open(data.authorizationUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      showAlert?.('error', errorMessage(error, 'Não foi possível iniciar o OAuth.'));
    } finally { setBusy(false); }
  };

  const completeOAuth = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { data } = await FinanceService.completeMercadoPagoOAuth(account.id, {
        code: oauth.code,
        state: oauth.state,
      });
      await connected(data);
    } catch (error) {
      showAlert?.('error', errorMessage(error, 'Não foi possível concluir o OAuth.'));
    } finally { setBusy(false); }
  };

  return <>
    <button type="button" onClick={() => setOpen(true)} className="inline-flex rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-700 hover:bg-sky-100">
      Conectar Mercado Pago
    </button>
    {open && <div role="dialog" aria-modal="true" aria-label="Conectar Mercado Pago" className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/60 p-4">
      <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl">
        <h3 className="text-xl font-semibold text-slate-900">Conectar Mercado Pago</h3>
        {!advanced ? <form onSubmit={connectDirect}>
          <p className="mt-2 text-sm text-slate-600">Informe o Access Token da sua conta. Ele será validado diretamente na API oficial, armazenado criptografado e não será exibido novamente.</p>
          <label htmlFor="mercado-pago-access-token" className="mt-5 block text-sm font-medium text-slate-700">Access Token</label>
          <input id="mercado-pago-access-token" type="password" autoComplete="off" required value={accessToken} onChange={(e) => setAccessToken(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" placeholder="APP_USR-…" />
          <div className="mt-5 flex flex-wrap gap-2">
            <button disabled={busy} className="rounded-full bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{busy ? 'Validando…' : 'Validar, conectar e sincronizar'}</button>
            <button type="button" onClick={() => setAdvanced(true)} className="rounded-full border px-4 py-2 text-sm text-slate-600">Modo avançado (OAuth)</button>
            <button type="button" onClick={() => { setAccessToken(''); setOpen(false); }} className="rounded-full border px-4 py-2 text-sm text-slate-600">Cancelar</button>
          </div>
        </form> : <form onSubmit={authorizationUrl ? completeOAuth : startOAuth}>
          <p className="mt-2 text-sm text-slate-600">Use OAuth somente para conectar contas de terceiros. Client ID e Client Secret pertencem à sua aplicação Mercado Pago.</p>
          {!authorizationUrl ? <div className="mt-4 grid gap-3">
            <input required value={oauth.clientId} onChange={(e) => setOauth({ ...oauth, clientId: e.target.value })} className="rounded-xl border px-3 py-2" placeholder="Client ID" />
            <input type="password" autoComplete="off" required value={oauth.clientSecret} onChange={(e) => setOauth({ ...oauth, clientSecret: e.target.value })} className="rounded-xl border px-3 py-2" placeholder="Client Secret" />
            <input type="url" required value={oauth.redirectUri} onChange={(e) => setOauth({ ...oauth, redirectUri: e.target.value })} className="rounded-xl border px-3 py-2" placeholder="https://seu-dominio/retorno" />
          </div> : <div className="mt-4 grid gap-3">
            <a href={authorizationUrl} target="_blank" rel="noreferrer" className="text-sm font-semibold text-sky-700 underline">Abrir autorização do Mercado Pago</a>
            <input required value={oauth.code} onChange={(e) => setOauth({ ...oauth, code: e.target.value })} className="rounded-xl border px-3 py-2" placeholder="Código retornado pelo Mercado Pago" />
          </div>}
          <div className="mt-5 flex gap-2">
            <button disabled={busy} className="rounded-full bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{authorizationUrl ? 'Concluir OAuth' : 'Iniciar OAuth'}</button>
            <button type="button" onClick={() => { setAuthorizationUrl(''); setAdvanced(false); }} className="rounded-full border px-4 py-2 text-sm text-slate-600">Voltar</button>
          </div>
        </form>}
      </div>
    </div>}
  </>;
}
